import { useMemo, useState } from "react";

import { Button } from "../../../../components/ui/Button";
import { weekdayLabel } from "../../../../lib/format";
import { t } from "../../../../lib/i18n";

const WEEKDAYS = [0, 1, 2, 3, 4, 5, 6] as const;

interface WeekdayCopyControlProps {
  /** Weekdays that currently have at least one window — only these can be sources. */
  populatedWeekdays: ReadonlyArray<number>;
  /** Invoked with chosen source + targets when the user clicks Copy. */
  onCopy: (sourceWeekday: number, targetWeekdays: number[]) => void;
  busy?: boolean;
}

export function WeekdayCopyControl({
  populatedWeekdays,
  onCopy,
  busy,
}: WeekdayCopyControlProps) {
  const populated = useMemo(() => new Set(populatedWeekdays), [populatedWeekdays]);
  const [source, setSource] = useState<number | null>(() =>
    populatedWeekdays.length > 0 ? populatedWeekdays[0] : null,
  );
  const [targets, setTargets] = useState<Set<number>>(() => new Set());

  if (populated.size === 0) return null;

  const toggleTarget = (wd: number) => {
    if (wd === source) return;
    setTargets((prev) => {
      const next = new Set(prev);
      if (next.has(wd)) next.delete(wd);
      else next.add(wd);
      return next;
    });
  };

  const canCopy = source !== null && targets.size > 0 && !busy;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 8,
        padding: "8px 12px",
        marginBottom: 12,
        border: "1px solid var(--color-border)",
        borderRadius: 6,
        background: "var(--color-surface-muted)",
      }}
    >
      <label
        style={{ fontSize: 12, color: "var(--color-text-muted)", marginRight: 4 }}
      >
        {t("schedules.weekdayCopy.source")}
      </label>
      <select
        className="input"
        value={source ?? ""}
        onChange={(e) => {
          const next = e.target.value === "" ? null : Number(e.target.value);
          setSource(next);
          if (next !== null) {
            setTargets((prev) => {
              const copy = new Set(prev);
              copy.delete(next);
              return copy;
            });
          }
        }}
        style={{ height: 28, padding: "0 8px", fontSize: 13 }}
      >
        {populatedWeekdays.map((wd) => (
          <option key={wd} value={wd}>
            {weekdayLabel(wd)}
          </option>
        ))}
      </select>
      <span style={{ fontSize: 12, color: "var(--color-text-muted)" }}>
        {t("schedules.weekdayCopy.targets")}
      </span>
      {WEEKDAYS.map((wd) => {
        const isSource = wd === source;
        const isTarget = targets.has(wd);
        return (
          <button
            key={wd}
            type="button"
            disabled={isSource || busy}
            onClick={() => toggleTarget(wd)}
            style={{
              padding: "4px 10px",
              fontSize: 12,
              borderRadius: 999,
              border: "1px solid",
              borderColor: isTarget
                ? "var(--color-accent)"
                : "var(--color-border-strong)",
              background: isTarget
                ? "var(--color-accent-soft)"
                : "var(--color-surface)",
              color: isSource ? "var(--color-text-muted)" : "var(--color-text)",
              cursor: isSource || busy ? "not-allowed" : "pointer",
              opacity: isSource ? 0.5 : 1,
            }}
          >
            {weekdayLabel(wd)}
          </button>
        );
      })}
      <Button
        size="sm"
        onClick={() => {
          if (source === null || targets.size === 0) return;
          onCopy(source, Array.from(targets));
          setTargets(new Set());
        }}
        disabled={!canCopy}
      >
        {t("schedules.weekdayCopy.button")}
      </Button>
    </div>
  );
}
